import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { envoyerPieceDossier } from '../../../hooks/useDossierExposant'
import { useTheme } from '../../../theme'
import { classeChamp } from '../champs'
import redimensionnerImage from './redimensionnerImage'
import { ORANGE, VERT, VERT_OK, CREME, ROUGE_TEXTE, teinte } from './dossierUi'

/**
 * Feuille « Ajouter une pièce » (prototype v7.5, DESIGN-GUIDE §11.5).
 * Deux entrees : l'appareil photo direct, ou un fichier (PDF ou image).
 * Apercu, date de fin de validite (exigee pour l'assurance), envoi.
 * Chaque erreur de l'API s'affiche en clair (format, poids, date).
 */
function FeuilleDepot({ piece, onClose, onEnvoye }) {
  const { isWeb3 } = useTheme()
  const [fichier, setFichier] = useState(null) // { file, nom, apercu }
  const [validite, setValidite] = useState(
    piece.dateValidite && piece.statut !== 'REFUSEE' && piece.statut !== 'EXPIREE' ? piece.dateValidite : '',
  )
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState(null)

  // L'apercu est une URL objet : on la libere en changeant de fichier / en fermant.
  useEffect(() => () => { if (fichier?.apercu) URL.revokeObjectURL(fichier.apercu) }, [fichier])

  const choisir = (e) => {
    const f = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!f) return
    setErreur(null)
    setFichier({ file: f, nom: f.name, apercu: f.type.startsWith('image/') ? URL.createObjectURL(f) : null })
  }

  const pret = !!fichier && (!piece.validiteRequise || !!validite) && !envoi

  const envoyer = async () => {
    setEnvoi(true)
    setErreur(null)
    try {
      const allege = await redimensionnerImage(fichier.file)
      if (allege.size > 10 * 1024 * 1024) {
        throw new Error('Fichier trop lourd : 10 Mo maximum. Essayez une photo, ou un PDF plus léger.')
      }
      const etat = await envoyerPieceDossier(piece.code, allege, piece.validiteRequise ? validite : null)
      onEnvoye(etat)
    } catch (e) {
      setErreur(e.message || 'Envoi impossible. Réessayez.')
      setEnvoi(false)
    }
  }

  const btnFichier = 'flex items-center gap-3 w-full rounded-[18px] cursor-pointer text-left'

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end"
      role="dialog"
      aria-modal="true"
      style={{ background: 'rgba(4,25,24,0.5)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full overflow-y-auto panel-slide-up"
        style={{
          background: CREME, borderRadius: '28px 28px 0 0', maxHeight: '88dvh',
          padding: '18px 20px calc(24px + env(safe-area-inset-bottom))',
        }}
      >
        <div className="w-[42px] h-1 rounded-full mx-auto mb-4" style={{ background: 'rgba(7,62,61,0.15)' }} />
        <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: 2, color: ORANGE }}>Ajouter une pièce</p>
        <p className="font-serif text-[23px] mt-[3px]" style={{ color: VERT }}>{piece.emoji} {piece.libelle}</p>
        <p className="text-[12.5px] mt-0.5" style={{ color: 'rgba(7,62,61,0.6)' }}>{piece.preuve}</p>

        {!fichier ? (
          <div className="flex flex-col gap-2.5 mt-[18px]">
            <label className={btnFichier} style={{ padding: '15px 16px', background: VERT, color: CREME }}>
              <input type="file" accept="image/*" capture="environment" onChange={choisir} className="hidden" />
              <span className="text-2xl">📷</span>
              <span className="flex-1">
                <span className="block text-[15px] font-extrabold">Prendre une photo</span>
                <span className="block text-[11.5px] opacity-75">Le document devant vous ? C'est le plus rapide.</span>
              </span>
            </label>
            <label className={`${btnFichier} glass-surface`} style={{ padding: '15px 16px', color: VERT }}>
              <input type="file" accept="application/pdf,image/*" onChange={choisir} className="hidden" />
              <span className="text-2xl">📎</span>
              <span className="flex-1">
                <span className="block text-[15px] font-extrabold">Choisir un fichier</span>
                <span className="block text-[11.5px]" style={{ color: 'rgba(7,62,61,0.55)' }}>PDF ou image, depuis le téléphone ou l'ordinateur</span>
              </span>
            </label>
            <p className="text-[11.5px] leading-relaxed mx-0.5 mt-0.5" style={{ color: 'rgba(7,62,61,0.55)' }}>
              💡 Posez le document à plat, bien éclairé, lisible en entier.
            </p>
          </div>
        ) : (
          <div className="glass-surface rounded-[18px] mt-[18px] flex items-center gap-3" style={{ padding: '12px 14px' }}>
            {fichier.apercu
              ? <img src={fichier.apercu} alt="" className="w-[54px] h-[54px] rounded-xl object-cover flex-shrink-0" />
              : <span className="w-[54px] h-[54px] rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ background: teinte(ORANGE, 0.14) }}>📄</span>}
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-bold truncate" style={{ color: VERT }}>{fichier.nom}</p>
              <p className="text-[11px] font-bold" style={{ color: VERT_OK }}>✓ Prêt à envoyer</p>
            </div>
            <button onClick={() => setFichier(null)} disabled={envoi} className="text-[12px] font-bold flex-shrink-0" style={{ color: ORANGE }}>
              Changer
            </button>
          </div>
        )}

        {piece.validiteRequise && (
          <div className="mt-3.5">
            <p className="text-[11px] font-bold mb-1.5" style={{ color: 'rgba(7,62,61,0.5)' }}>Date de fin de validité *</p>
            <input type="date" value={validite} onChange={(e) => setValidite(e.target.value)} className={classeChamp(isWeb3)} />
            <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: 'rgba(7,62,61,0.5)' }}>
              Elle figure sur l'attestation. On vous préviendra avant l'échéance.
            </p>
          </div>
        )}

        <p className="text-[11px] leading-relaxed mt-3.5" style={{ color: 'rgba(7,62,61,0.5)' }}>
          🔒 Vérifiée par l'équipe Manrina, transmissible à la mairie ou au gestionnaire du lieu pour l'autorisation du marché. Jamais publique.
        </p>
        {erreur && (
          <p role="alert" className="text-[12.5px] font-semibold leading-relaxed mt-3 rounded-xl" style={{ color: ROUGE_TEXTE, background: 'rgba(217,83,79,0.08)', padding: '10px 12px' }}>
            {erreur}
          </p>
        )}
        <button
          disabled={!pret}
          onClick={envoyer}
          className="mt-3.5 w-full rounded-2xl text-[15px] font-extrabold"
          style={{ padding: 15, background: pret ? VERT : 'rgba(7,62,61,0.18)', color: CREME }}
        >
          {envoi ? 'Envoi…' : 'Envoyer pour vérification'}
        </button>
        <button onClick={onClose} className="mt-2 w-full rounded-2xl text-[13px] font-bold" style={{ padding: 12, color: 'rgba(7,62,61,0.5)' }}>
          Annuler
        </button>
      </div>
    </div>,
    document.body,
  )
}

export default FeuilleDepot
